import { Checkbox, EmptyState, InfoIcon, ModalBottomButtons } from '@bp/ui-components';
import classNames from 'classnames';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  ClassTutorsConnectFieldInput,
  InputMaybe,
  SortDirection,
  use_ClassesQuery,
  useCreateClassMutation,
} from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { showSuccessToast, showUserErrorToast } from '../../../utils/toast';
import styles from './InheritClassesForm.module.scss';
import { InheritClassesFormRow } from './InheritClassesFormRow';

export type InheritClassesFormType = {
  uuid: string;
  name: string;
  shortName: string;
  grade: number | null | undefined;
  gradeName: string | null;
  gradeGroupUuid: string | null;
  gradeGroupName: string | null;
  certificatesEnabled: boolean;
  tutorUuid1: string | null;
  tutorName1: string | null;
  tutorUuid2: string | null;
  tutorName2: string | null;
  tutorUuid3: string | null;
  tutorName3: string | null;
  color?: string;
  colorLabel?: string;
  printOrder?: number | null;
  printPageNr?: number | null;
  roomUuid: string | null;
  roomName: string | null;
  selected: boolean;
  inheritColor: boolean;
}[];

type InheritClassesFormProps = {
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

export const InheritClassesForm = ({ onLoad, onClose }: InheritClassesFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const previousSchoolYear = useUserConfigContext().previousSchoolYear;
  const classContext = useMemorizedCacheTag('CLASSES_FIXED');

  const [, createClass] = useCreateClassMutation();

  const { roomsData, gradeGroupData, teacherData } = useLoadBasicData({ pause: !previousSchoolYear });

  const [{ data: classesData }] = use_ClassesQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: previousSchoolYear?.uuid,
        },
      },
      options: {
        sort: [
          {
            sortName: SortDirection.Asc,
          },
        ],
      },
    },
    pause: !previousSchoolYear,
    context: classContext,
  });

  const lastYearClasses: InheritClassesFormType = useMemo(() => {
    return (
      classesData?.classes.map((c) => {
        const gradeGroup = gradeGroupData?.gradeGroups.find((gg) => gg.uuid === c.gradeGroup?.uuid);
        const room = roomsData?.rooms.find((room) => room.uuid === c.defaultRoom?.uuid);
        const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');

        // get tutors from class within last school year, so they can 'grow' with the class
        // example: tutor1 in class 1a should be tutor1 in class 2a
        let tutor1, tutor2, tutor3;
        const grade = c.grade;
        if (grade && grade <= 14) {
          const newGrade = grade - 1;
          const originalClass = classesData.classes.find(
            (c) => c.grade === newGrade && c.gradeGroup?.uuid === gradeGroup?.uuid,
          );
          tutor1 = teacherData?.people.find(
            (t) => t.uuid === originalClass?.tutorsConnection.edges[0]?.node.uuid && t.active,
          );
          tutor2 = teacherData?.people.find(
            (t) => t.uuid === originalClass?.tutorsConnection.edges[1]?.node.uuid && t.active,
          );
          tutor3 = teacherData?.people.find(
            (t) => t.uuid === originalClass?.tutorsConnection.edges[2]?.node.uuid && t.active,
          );
        }

        return {
          uuid: c.uuid,
          name: c.name,
          shortName: c.shortName,
          grade: c.grade ?? null,
          gradeName: c.grade ? c.grade.toString() : 'F',
          gradeGroupUuid: gradeGroup?.uuid ?? null,
          gradeGroupName: gradeGroup?.name ?? null,
          certificatesEnabled: c.certificatesEnabled ?? false,
          tutorUuid1: tutor1?.uuid ?? null,
          tutorName1: tutor1?.displayNameShort ?? null,
          tutorUuid2: tutor2?.uuid ?? null,
          tutorName2: tutor2?.displayNameShort ?? null,
          tutorUuid3: tutor3?.uuid ?? null,
          tutorName3: tutor3?.displayNameShort ?? null,
          color: html,
          colorLabel: label,
          printOrder: c.timetableConfig?.printOrder ?? null,
          printPageNr: c.timetableConfig?.printPageNr ?? null,
          roomUuid: room?.uuid ?? null,
          roomName: room?.name ?? null,
          selected: false,
          inheritColor: true,
        };
      }) ?? []
    );
  }, [classesData, gradeGroupData?.gradeGroups, roomsData?.rooms, teacherData?.people]);

  async function handleSubmit(values: InheritClassesFormType, helper: FormikHelpers<InheritClassesFormType>) {
    onLoad(true);

    const classesToInherit = values.filter((c) => c.selected);

    let inheritCount = 0;

    for (const cl of classesToInherit) {
      const tutors: InputMaybe<Array<ClassTutorsConnectFieldInput>> = [];
      if (cl.tutorUuid1) {
        tutors.push({
          edge: {
            order: 1,
          },
          where: {
            node: {
              uuid: cl.tutorUuid1,
            },
          },
        });
      }
      if (cl.tutorUuid2) {
        tutors.push({
          edge: {
            order: 2,
          },
          where: {
            node: {
              uuid: cl.tutorUuid2,
            },
          },
        });
      }
      if (cl.tutorUuid3) {
        tutors.push({
          edge: {
            order: 3,
          },
          where: {
            node: {
              uuid: cl.tutorUuid3,
            },
          },
        });
      }

      const baseData = {
        shortName: cl.shortName,
        name: cl.name,
        grade: cl.grade,
        certificatesEnabled: cl.certificatesEnabled,
      };

      const result = await createClass(
        {
          input: {
            ...baseData,
            defaultRoom: {
              connect: { where: { node: { uuid: cl.roomUuid ?? '' } } },
            },
            organization: {
              connect: {
                where: {
                  node: {
                    uuid: pimAuthClaims.getOrganizationUuid(),
                  },
                },
              },
            },
            schoolYear: {
              connect: {
                where: {
                  node: {
                    uuid: currentSchoolYear?.uuid,
                  },
                },
              },
            },
            tutors: { connect: tutors },
            timetableConfig: {
              create: {
                node: {
                  color: cl.inheritColor ? cl.color : undefined,
                  colorLabel: cl.inheritColor ? cl.colorLabel : undefined,
                  printOrder: cl.printOrder,
                  printPageNr: cl.printPageNr,
                  isEnabled: true,
                  organization: {
                    connect: {
                      where: {
                        node: {
                          uuid: pimAuthClaims.getOrganizationUuid(),
                        },
                      },
                    },
                  },
                },
              },
            },
            gradeGroup: {
              connect: { where: { node: { uuid: cl.gradeGroupUuid ?? '' } } },
            },
          },
        },
        classContext,
      );

      if (result.error) {
        showUserErrorToast({ error: result.error });
        break;
      } else {
        inheritCount++;
      }
    }

    if (inheritCount > 0) {
      showSuccessToast(t('classes.inheritSuccess', { count: inheritCount }));
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  }

  return !previousSchoolYear || lastYearClasses.length === 0 ? (
    <EmptyState
      title={!previousSchoolYear ? t('common.noPreviousYear') : t('classes.noPreviousClasses')}
      icon={<InfoIcon />}
    />
  ) : (
    <Formik<InheritClassesFormType> initialValues={lastYearClasses} onSubmit={handleSubmit}>
      {({ values, isSubmitting, resetForm, setValues }) => {
        return (
          <Form className={styles['inherit-classes-form']}>
            <div className={styles.header}>
              <div className={styles.checkbox}>
                <Checkbox
                  name='select-all'
                  checked={values.filter((d) => d.selected).length === values.length}
                  onChange={async () => {
                    let selected = false;
                    if (values.filter((v) => v.selected).length === values.length) {
                      selected = false;
                    } else {
                      selected = true;
                    }
                    await setValues((values) => {
                      return values.map((v) => {
                        return { ...v, selected };
                      });
                    });
                  }}
                />
              </div>
              <div className={styles.medium}>{t('common.name')}</div>
              <div className={styles.small}>{t('classes.level.short')}</div>
              <div className={styles.small}>{t('gradeGroups.title', { context: 'short' })}</div>
              <div className={styles['short-name']}>{t('classes.shortName')}</div>
              <div className={styles.large}>{`${t('classes.tutor.short')} 1`}</div>
              <div className={styles.large}>{`${t('classes.tutor.short')} 2`}</div>
              <div className={styles.large}>{`${t('classes.tutor.short')} 3`}</div>
              <div className={styles.large}>{t('rooms.title', { count: 1 })}</div>
              <div className={styles.small}>{t('common.color')}</div>
              <div className={styles.small}>{t('classes.inheritColor')}?</div>
            </div>

            <div className={classNames(styles.body, 'has-scrollbar')}>
              <FieldArray
                name='classes'
                render={(arrayHelpers) => {
                  return values.map((deputat, index) => (
                    <InheritClassesFormRow key={deputat.uuid} index={index} helper={arrayHelpers} />
                  ));
                }}
              />
            </div>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: isSubmitting || values.filter((d) => d.selected).length === 0 }}
              className='px-6'
            />
          </Form>
        );
      }}
    </Formik>
  );
};
